if (!customElements.get('wls-header')) {
  class WLSHeader extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'hidden',
        megaMenuToggle: 'js-wls-megamenu-toggle',
        megaMenuDropdown: 'js-wls-megamenu-dropdown',
        sticky: 'wls-header--sticky',
        stickyScrollUp: 'wls-header--sticky-scroll-up',
        section: 'wls-header-section',
        megaMenuOverlay: 'js-wls-megamenu-dropdown-overlay',
        headerSearchOverlay: 'js-wls-header-search-overlay',
        mobileMenuToggle: 'js-mobile-menu-toggle',
        headerMenuWrapper: 'js-header-menu-wrapper',
        megaMenuBackBtn: 'js-megamenu-back-btn',
        megaMenuBlocks: 'js-megamenu-blocks',
        swiperWrapper: 'js-swiper-wrapper',
        contentWrapper: 'js-content-wrapper',
        megaMenuLabel: 'js-megamenu-label',
        headerSearchToggle: 'js-wls-header-search-toggle',
        headerSearchCloseBtn: 'wls-search-form__input-clear-btn',
        headerSearch: 'wls-header-search',
        lockScroll: 'wls-lock-scroll',
        wlsAccordionElement: 'wls-accordion',
        wlsPredictiveSearchElement: 'wls-predictive-search',
      };

      this.selectors = {
        megaMenuToggles: this.querySelectorAll(`.${this.classes.megaMenuToggle}`),
        headerSection: document.querySelector(`.${this.classes.section}`),
        megaMenuOverlay: this.querySelector(`.${this.classes.megaMenuOverlay}`),
        headerSearchOverlay: this.querySelector(`.${this.classes.headerSearchOverlay}`),
        megaMenuDropdowns: this.querySelectorAll(`.${this.classes.megaMenuDropdown}`),
        mobileMenuToggles: this.querySelectorAll(`.${this.classes.mobileMenuToggle}`),
        headerMenuWrapper: this.querySelector(`.${this.classes.headerMenuWrapper}`),
        megaMenuBackBtns: this.querySelectorAll(`.${this.classes.megaMenuBackBtn}`),
        headerSearchToggles: this.querySelectorAll(`.${this.classes.headerSearchToggle}`),
        headerSearch: this.querySelector(`${this.classes.headerSearch}`),
        headerSearchCloseBtn: this.querySelector(`.${this.classes.headerSearchCloseBtn}`),
      };

      this.behavior = this.dataset.headerBehavior || '';
      this.lastScroll = window.scrollY;
      this.isHeaderSearchOpen = false;
      this.isMegaMenuOpen = false;
      this.isMobileMenuOpen = false;
      this.boundHandleMegaMenuBlocks = debounce(this.handleMegaMenuBlocks.bind(this), 200);
    }

    connectedCallback() {
      this.bindEvents();
      if (this.selectors.headerSection) {
        this.selectors.headerSection.classList.remove(this.classes.sticky, this.classes.stickyScrollUp);
        this.handleHeaderBehavior();
      }
      this.setHeaderHeight();
      this.handleMegaMenuBlocks();
      this.cloneMegaMenuLabel();
      window.addEventListener('resize', this.boundHandleMegaMenuBlocks);
    }

    bindEvents() {
      // Header search
      if (this.selectors.headerSearchToggles.length > 0) {
        this.selectors.headerSearchToggles.forEach((toggle) => {
          toggle.addEventListener('click', this.toggleHeaderSearch.bind(this));
        });
      }

      if (this.selectors.headerSearchCloseBtn) {
        this.selectors.headerSearchCloseBtn.addEventListener('click', this.closeHeaderSearch.bind(this));
      }

      // Desktop mega menu
      if (this.selectors.megaMenuToggles.length > 0) {
        this.selectors.megaMenuToggles.forEach((toggle) => {
          toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleMegaMenu(toggle);
          });
        });
      }

      // Overlay click handle for all modals
      if (this.selectors.megaMenuOverlay) {
        this.selectors.megaMenuOverlay.addEventListener('click', this.onMegaMenuOverlayClick.bind(this));
      }
      if (this.selectors.headerSearchOverlay) {
        this.selectors.headerSearchOverlay.addEventListener('click', this.onHeaderSearchOverlayClick.bind(this));
      }

      // ESC key handle for all modals
      document.addEventListener('keydown', this.onEscKey.bind(this));

      if (window.innerWidth > 1023) {
        document.addEventListener('click', (e) => {
          if (e.target.closest(`.${this.classes.megaMenuDropdown}`)) {
            return;
          }
          if (e.target.closest(`.${this.classes.section}`)) {
            if (
              e.target.closest(`.${this.classes.headerSearch}`) ||
              e.target.closest(`.${this.classes.headerSearchOverlay}`)
            ) {
              return;
            }
            if (this.isMegaMenuOpen && !this.isHeaderSearchOpen && !this.isMobileMenuOpen) {
              this.closeMegaMenu();
            }
          }
        });
      }

      // Mobile menu
      if (window.innerWidth < 1023) {
        this.selectors.mobileMenuToggles.forEach((toggle) => {
          toggle.addEventListener('click', this.mobileMenuToggle.bind(this));
        });
        this.selectors.megaMenuBackBtns.forEach((backBtn) => {
          backBtn.addEventListener('click', this.megaMenuBackBtn.bind(this, backBtn));
        });
      }
    }

    toggleHeaderSearch(trigger) {
      if (!trigger) return;
      this.lockScroll();
      if (this.isHeaderSearchOpen) {
        this.closeHeaderSearch();
      } else {
        this.isHeaderSearchOpen = true;
        this.selectors.headerSearch.classList.add(this.classes.active);
        this.updateOverlay();
      }
    }

    closeHeaderSearch() {
      if (!this.isHeaderSearchOpen) return;
      this.isHeaderSearchOpen = false;
      this.selectors.headerSearch.classList.remove(this.classes.active);
      this.updateOverlay();
      if (!this.isMegaMenuOpen && !this.isMobileMenuOpen) {
        this.unlockScroll();
      }
      this.querySelectorAll(`${this.classes.wlsPredictiveSearchElement}`).forEach((el) => {
        el.dispatchEvent(new CustomEvent('wls:predictive-search:close'));
      });
    }

    toggleMegaMenu(trigger) {
      const isActive = trigger.classList.contains(this.classes.active);
      this.closeMegaMenu();
      if (!isActive) {
        trigger.classList.add(this.classes.active);
        const dropdown = trigger.nextElementSibling;
        dropdown.classList.add(this.classes.active);
        this.isMegaMenuOpen = true;
        this.updateOverlay();
        this.lockScroll();
      } else {
        this.isMegaMenuOpen = false;
        this.updateOverlay();
        this.unlockScroll();
      }
    }

    cloneMegaMenuLabel() {
      this.selectors.megaMenuToggles.forEach((toggle) => {
        const label = toggle.dataset.label;
        const dropdown = toggle.nextElementSibling;
        const labelClone = dropdown.querySelector(`.${this.classes.megaMenuLabel}`);
        labelClone.textContent = label;
      });
    }

    megaMenuBackBtn(trigger) {
      const dropdown = trigger.closest(`.${this.classes.megaMenuDropdown}`);
      dropdown.classList.remove(this.classes.active);
      this.selectors.megaMenuToggles.forEach((toggle) => {
        toggle.classList.remove(this.classes.active);
      });
      this.isMegaMenuOpen = false;
      this.updateOverlay();
    }

    mobileMenuToggle() {
      if (this.selectors.headerMenuWrapper.classList.contains(this.classes.active)) {
        this.selectors.headerMenuWrapper.classList.remove(this.classes.active);
        this.selectors.megaMenuOverlay.classList.remove(this.classes.active);
        this.unlockScroll();
        this.isMobileMenuOpen = false;
        this.updateOverlay();
        const menuWrapper = this.selectors.headerMenuWrapper;
        if (menuWrapper) {
          menuWrapper.querySelectorAll(`${this.classes.wlsAccordionElement}`).forEach((acc) => {
            acc.dispatchEvent(new CustomEvent('wls:accordion:close-all', { bubbles: false }));
          });
        }
      } else {
        this.selectors.headerMenuWrapper.classList.add(this.classes.active);
        this.selectors.megaMenuOverlay.classList.add(this.classes.active);
        this.lockScroll();
        this.isMobileMenuOpen = true;
        this.updateOverlay();
      }
    }

    closeMobileMenuResetState() {
      this.selectors.headerMenuWrapper.classList.remove(this.classes.active);
      this.selectors.megaMenuOverlay.classList.remove(this.classes.active);
      this.unlockScroll();
      this.isMobileMenuOpen = false;
      this.updateOverlay();
      const menuWrapper = this.selectors.headerMenuWrapper;
      if (menuWrapper) {
        menuWrapper.querySelectorAll('wls-accordion').forEach((acc) => {
          acc.dispatchEvent(new CustomEvent('wls:accordion:close-all', { bubbles: false }));
        });
      }
    }

    closeMegaMenu() {
      const elementsToReset = [
        ...this.selectors.megaMenuToggles,
        ...this.selectors.megaMenuDropdowns,
        this.selectors.megaMenuOverlay,
      ];
      elementsToReset.forEach((element) => {
        element.classList.remove(this.classes.active);
      });
      this.isMegaMenuOpen = false;
      this.updateOverlay();
      this.unlockScroll();
    }

    handleHeaderBehavior() {
      if (this.behavior === 'always_sticky') {
        this.selectors.headerSection.classList.add(this.classes.sticky);
      } else if (this.behavior === 'sticky_scroll_up') {
        this.selectors.headerSection.classList.add(this.classes.stickyScrollUp);
        this.handleScroll = this.handleScroll.bind(this);
        window.addEventListener('scroll', this.handleScroll);
      }
    }

    handleScroll() {
      if (!this.selectors.headerSection) return;
      const currentScroll = window.scrollY;
      this.selectors.headerSection.classList.toggle(
        this.classes.hidden,
        currentScroll > this.lastScroll && currentScroll > 50
      );
      this.lastScroll = currentScroll;
    }

    disconnectedCallback() {
      if (this.behavior === 'sticky_scroll_up') {
        window.removeEventListener('scroll', this.handleScroll);
      }
      document.removeEventListener('keydown', this.handleEscKey);
      window.removeEventListener('resize', this.boundHandleMegaMenuBlocks);
    }

    setHeaderHeight() {
      const headerHeight = this.offsetHeight;
      document.documentElement.style.setProperty('--wls-header-height', `${headerHeight}px`);
    }

    handleMegaMenuBlocks() {
      this.selectors.megaMenuDropdowns.forEach((megaMenuDropdown) => {
        megaMenuDropdown.querySelectorAll('wls-megamenu-dropdown').forEach((dropdown) => {
          const blocks = dropdown.querySelector(`.${this.classes.megaMenuBlocks}`);
          const swiperWrapper = dropdown.querySelector(`.${this.classes.swiperWrapper}`);
          const contentWrapper = dropdown.querySelector(`.${this.classes.contentWrapper}`);
          const swiper = dropdown.querySelector('wls-slider');
          if (!blocks || !swiperWrapper || !contentWrapper) return;

          swiperWrapper.innerHTML = '';
          contentWrapper.innerHTML = '';

          const blockNodes = Array.from(blocks.children);
          if (window.innerWidth > 1023) {
            blockNodes.forEach((node) => swiperWrapper.appendChild(node.cloneNode(true)));
            if (swiper) {
              swiper.dispatchEvent(new CustomEvent('wls:slider:reinit'));
            }
          } else {
            blockNodes.forEach((node) => contentWrapper.appendChild(node.cloneNode(true)));
          }
        });
      });
    }

    lockScroll() {
      document.body.classList.add(this.classes.lockScroll);
    }

    unlockScroll() {
      document.body.classList.remove(this.classes.lockScroll);
    }

    onMegaMenuOverlayClick(e) {
      if (this.isMobileMenuOpen) {
        this.closeMobileMenuResetState();
      } else if (this.isMegaMenuOpen) {
        this.closeMegaMenu();
      }
    }

    onHeaderSearchOverlayClick(e) {
      if (this.isHeaderSearchOpen) {
        this.closeHeaderSearch();
      }
    }

    onEscKey(e) {
      if (e.key === 'Escape') {
        if (this.isHeaderSearchOpen) {
          this.closeHeaderSearch();
          return;
        }
        if (this.isMegaMenuOpen) {
          this.closeMegaMenu();
          return;
        }
        if (this.isMobileMenuOpen) {
          this.closeMobileMenuResetState();
        }
      }
    }

    updateOverlay() {
      if (this.selectors.headerSearchOverlay) {
        if (this.isHeaderSearchOpen) {
          this.selectors.headerSearchOverlay.classList.add(this.classes.active);
        } else {
          this.selectors.headerSearchOverlay.classList.remove(this.classes.active);
        }
      }
      if (this.selectors.megaMenuOverlay) {
        if (this.isMegaMenuOpen || this.isMobileMenuOpen) {
          this.selectors.megaMenuOverlay.classList.add(this.classes.active);
        } else {
          this.selectors.megaMenuOverlay.classList.remove(this.classes.active);
        }
      }
    }
  }
  customElements.define('wls-header', WLSHeader);
}
